import { DateTime } from 'luxon';
import { assert } from '@ember/debug';

import OnDemandStatus from './on-demand-status.ts';
import { BILLING_ACCOUNT_STATUS } from '../constants/status.ts';

import type {
  Billing20201105BillingAccount,
  Billing20201105OnDemandBillingMethodDetails,
  Billing20201105CardDetails,
} from '@clients/cloud-billing';

//Helpers
function isPaymentExpired({ expMonth, expYear }: Billing20201105CardDetails) {
  if (!expMonth || !expYear) {
    assert('Must provide expiry month and year');
  }
  const expiration = DateTime.fromObject({ month: expMonth, year: expYear })
    .toUTC()
    .endOf('month');
  return DateTime.utc() > expiration;
}

export default class OnDemand {
  #billingAccount: Billing20201105BillingAccount | null;
  #onDemandBillingMethodDetails?: Billing20201105OnDemandBillingMethodDetails | null;

  /**
   * OnDemand constructor
   *
   * Accepts both `null` and `undefined` for optional fields to handle real-world data sources (e.g., API responses, deserialization)
   * where properties may be missing or explicitly set to null. All missing values are normalized to `null` internally.
   * This ensures the rest of the codebase only needs to check for `null` (not both `null` and `undefined`).
   *
   * @param billingAccount - The billing account
   * @param onDemandBillingMethodDetails - The on-demand billing method details
   */
  constructor(
    billingAccount?: Billing20201105BillingAccount | null,
    onDemandBillingMethodDetails?: Billing20201105OnDemandBillingMethodDetails | null,
  ) {
    this.#billingAccount = billingAccount ?? null;
    this.#onDemandBillingMethodDetails = onDemandBillingMethodDetails ?? null;
  }

  /** Returns the card details or null if not present */
  get cardDetails() {
    return this.#onDemandBillingMethodDetails?.cardDetails || null;
  }

  /** Returns true if the card has an error (delinquent or expired) */
  get hasCardError() {
    return (
      this.#billingAccount?.status == BILLING_ACCOUNT_STATUS.DELINQUENT ||
      this.cardIsExpired
    );
  }

  /** Returns true if the card is expired */
  get cardIsExpired() {
    return !!this.cardDetails && isPaymentExpired(this.cardDetails);
  }

  /** Returns the on-demand status */
  get status() {
    return new OnDemandStatus(this.#billingAccount?.onDemandStatus);
  }

  /** Returns the billing method details or null if not present */
  get billingMethodDetails() {
    return this.#onDemandBillingMethodDetails?.billingMethod || null;
  }
}
